// signalr.ts
import * as signalR from '@microsoft/signalr';
import { API_URI } from '../constants';
import { getBaseUri } from './getBaseUri';

/**
 * Custom retry policy:
 * - First 4 attempts: 0s, 3s, 10s, 30s (fast recovery for transient blips)
 * - All subsequent attempts: every 60s indefinitely (server down / 1011 loop)
 * - Skips retry while the tab is hidden — resumes when the user returns
 */
class CappedRetryPolicy implements signalR.IRetryPolicy {
    private readonly fastDelays = [0, 3_000, 10_000, 30_000];
    
    private readonly capMs = 60_000;

    nextRetryDelayInMilliseconds(
        retryContext: signalR.RetryContext
    ): number | null {
        // Pause retries while the tab is not visible — avoids hammering
        // the server with negotiate calls the user will never see
        if (document.visibilityState === 'hidden') {
            return this.capMs;
        }

        const idx = retryContext.previousRetryCount;
        return idx < this.fastDelays.length ? this.fastDelays[idx] : this.capMs;
    }
}

export const createSignalRConnection = (userId: string) => {
    return new signalR.HubConnectionBuilder()
        .withUrl(`${getBaseUri(API_URI)}/hub/notifications?userId=${userId}`, {
            withCredentials: false,
        })
        .withAutomaticReconnect(new CappedRetryPolicy())
        .configureLogging(signalR.LogLevel.Warning)
        .build();
};
